import React, { memo, Component, ErrorInfo, ReactNode } from 'react';
import { Button, Space, Switch, Typography, Empty, Alert } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import Editor from "@monaco-editor/react";
import JsonViewer from '@src/components/Common/JsonViewer';
import DiffViewer from '@src/ui/DiffViewer';
import { PackPipelineEnhancedView } from './PackPipelineEnhancedView';
import { ExecutionStepsViewer } from './ConfigStage';
import { OptimizerSummaryPanel } from './ConfigStage/OptimizerSummaryPanel';
import { VerificationResultsPanel } from './VerifyStage';
import { ExecutionResultsViewer } from './ExecuteStage';
import { PipelineOverviewViewer } from './PipelineOverview';
import { ConfigData, VerifyData, ExecuteData } from './types';

// Error boundary for enhanced view components
interface EnhancedViewErrorBoundaryProps {
  children: ReactNode;
  fallbackData?: any;
  onSwitchToRaw?: () => void;
}

interface EnhancedViewErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class EnhancedViewErrorBoundary extends Component<EnhancedViewErrorBoundaryProps, EnhancedViewErrorBoundaryState> {
  constructor(props: EnhancedViewErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): EnhancedViewErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[EnhancedView] Rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '16px' }}>
          <Alert
            message="Enhanced view not compatible"
            description="The data structure doesn't match the expected format for enhanced rendering. Showing raw JSON instead."
            type="warning"
            icon={<WarningOutlined />}
            showIcon
            style={{ marginBottom: '16px' }}
            action={
              this.props.onSwitchToRaw && (
                <Button size="small" onClick={this.props.onSwitchToRaw}>
                  Switch to Raw JSON
                </Button>
              )
            }
          />
          {this.props.fallbackData && (
            <JsonViewer
              data={this.props.fallbackData}
              collapsed={false}
              fontSize="13px"
              lineHeight="1.6"
              padding="0px"
              backgroundColor="#fafafa"
              borderColor="#e8e8e8"
            />
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Label first, switch second: the switch stays anchored in place while the
// changing label grows leftward, so toggling never moves the control.
export const ViewModeSwitch = ({
  viewMode,
  onChange,
  labels
}: {
  viewMode: 'enhanced' | 'raw';
  onChange: (mode: 'enhanced' | 'raw') => void;
  labels?: { enhanced: string; raw: string };
}) => {
  const text = labels ?? { enhanced: 'Enhanced View', raw: 'Raw Data' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Typography.Text type="secondary" style={{ fontSize: 13, lineHeight: 1 }}>
        {viewMode === 'enhanced' ? text.enhanced : text.raw}
      </Typography.Text>
      <Switch
        size="small"
        checked={viewMode === 'enhanced'}
        onChange={(checked) => onChange(checked ? 'enhanced' : 'raw')}
      />
    </div>
  );
};

interface PackDataEnhancedRowProps {
  initialValue?: any;
  compareValue?: any;
  isCompareMode?: boolean;
  currentStage: number; // -1=overview, 0=config, 1=verify, 2=execution
  dataPipeline?: any;
  dataComparePipeline?: any;
  isEditing: boolean;
  editedJson: string;
  onEditedJsonChange: (value: string) => void;
  viewMode: 'enhanced' | 'raw';
  onViewModeChange: (mode: 'enhanced' | 'raw') => void;
  /* See PackDataExpandedRow: the way into editing is already gone in read-only
     mode, this makes sure the editor cannot render even so. */
  readOnly?: boolean;
}

export const PackDataEnhancedRow = memo(({
  initialValue = {},
  compareValue = null,
  isCompareMode = false,
  currentStage,
  dataPipeline,
  dataComparePipeline,
  isEditing,
  readOnly,
  editedJson,
  onEditedJsonChange,
  viewMode,
  onViewModeChange
}: PackDataEnhancedRowProps) => {
  // Parse the data
  let parsedData: any;
  if (initialValue == null)
    parsedData = {}
  else if (typeof initialValue === 'string')
    try {
      parsedData = JSON.parse(initialValue);
    } catch {
      parsedData = null;
    }
  else
    parsedData = initialValue;

  let parsedCompareData: any = null;
  if (isCompareMode && compareValue != null) {
    if (typeof compareValue === 'string') {
      try {
        parsedCompareData = JSON.parse(compareValue);
      } catch {
        parsedCompareData = null;
      }
    } else {
      parsedCompareData = compareValue;
    }
  }

  // Determine if we should use enhanced view
  // Use enhanced view for all stages (CONFIG, VERIFY, EXECUTE) when not in compare mode
  const shouldUseEnhancedView = !isCompareMode && !isEditing && (currentStage === 0 || currentStage === 1 || currentStage === 2);

  // If in compare mode, show diff viewer
  if (isCompareMode) {
    return (
      <DiffViewer
        oldValue={parsedData || {}}
        newValue={parsedCompareData || {}}
        leftTitle={`Pipeline${dataPipeline?.version ? ` ${dataPipeline.version}` : ''}`}
        rightTitle={`Pipeline${dataComparePipeline?.version ? ` ${dataComparePipeline.version}` : ''}`}
        compareMethod="lines"
      />
    );
  }

  // If editing, show Monaco editor (Submit/Cancel live in the pipeline info panel)
  if (isEditing && !readOnly) {
    return (
      <div style={{ flex: 1 }}>
        <Editor
          height="300px"
          defaultLanguage="json"
          value={editedJson}
          onChange={(value) => onEditedJsonChange(value || '')}
          options={{ minimap: { enabled: false } }}
        />
      </div>
    );
  }

  // If overview mode (currentStage === -1), show pipeline overview
  if (currentStage === -1 && !isCompareMode) {
    return (
      <div style={{ flex: 1 }}>
        <EnhancedViewErrorBoundary fallbackData={dataPipeline}>
          <PipelineOverviewViewer dataPipeline={dataPipeline} />
        </EnhancedViewErrorBoundary>
      </div>
    );
  }

  // If should use enhanced view
  if (shouldUseEnhancedView && viewMode === 'enhanced') {
    // Extract the right data based on current stage
    let configData: ConfigData | undefined;
    let verifyData: VerifyData | undefined;
    let executeData: ExecuteData | undefined;

    console.log('[PackDataEnhancedRow] Enhanced view mode');
    console.log('[PackDataEnhancedRow] Current stage:', currentStage);
    console.log('[PackDataEnhancedRow] Parsed data:', parsedData);
    console.log('[PackDataEnhancedRow] Parsed data type:', typeof parsedData);

    if (currentStage === 0) {
      // CONFIG stage
      configData = parsedData as ConfigData;
      console.log('[PackDataEnhancedRow] CONFIG stage - configData:', configData);
    } else if (currentStage === 1) {
      // VERIFY stage
      verifyData = parsedData as VerifyData;
      console.log('[PackDataEnhancedRow] VERIFY stage - verifyData:', verifyData);
    } else if (currentStage === 2) {
      // EXECUTION stage
      executeData = parsedData as ExecuteData;
      console.log('[PackDataEnhancedRow] EXECUTE stage - executeData:', executeData);
    }

    return (
      <div style={{ flex: 1 }}>
        {/* Enhanced view - render component directly based on stage */}
        <EnhancedViewErrorBoundary
          fallbackData={parsedData}
          onSwitchToRaw={() => onViewModeChange('raw')}
        >
          {currentStage === 0 && configData && (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <ExecutionStepsViewer data={configData} />
              {configData.optimizer_summary && (
                <OptimizerSummaryPanel
                  summary={configData.optimizer_summary}
                  requestRetrigger={configData.request_retrigger}
                />
              )}
            </Space>
          )}

          {currentStage === 1 && verifyData && (
            <VerificationResultsPanel data={verifyData} />
          )}

          {currentStage === 2 && executeData && (
            <ExecutionResultsViewer data={executeData} />
          )}

          {/* Fallback if no data */}
          {!configData && !verifyData && !executeData && (
            <Empty description="No data available for this stage" />
          )}
        </EnhancedViewErrorBoundary>
      </div>
    );
  }

  // Fallback: Show raw JSON viewer
  return (
    <div style={{ flex: 1 }}>
      {parsedData ? (
        <JsonViewer
          data={parsedData}
          collapsed={false}
          fontSize="13px"
          lineHeight="1.6"
          padding="0px"
          backgroundColor="#fafafa"
          borderColor="#e8e8e8"
        />
      ) : (
        <div>No data available</div>
      )}
    </div>
  );
});

export default PackDataEnhancedRow;
